import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageSquare, X, Send, Loader2, Sparkles, RotateCcw } from "lucide-react";
import api from "@/lib/api";

const SESSION_KEY = "smw_chat_session";
const MODEL_KEY = "smw_chat_model";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getSession = () => {
  let id = localStorage.getItem(SESSION_KEY);
  if (!id) {
    id = `web-${crypto.randomUUID()}`;
    localStorage.setItem(SESSION_KEY, id);
  }
  return id;
};

const SUGGESTIONS = ["What is Oil Trac?", "Do you convert Japanese BMW navigation?", "How do I see my service history?"];

export const AiChat = () => {
  const [open, setOpen] = useState(false);
  const [models, setModels] = useState([]);
  const [model, setModel] = useState(localStorage.getItem(MODEL_KEY) || "");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(getSession);
  const bottomRef = useRef(null);

  useEffect(() => {
    api.get("/ai/models").then(({ data }) => {
      setModels(data.models);
      if (!localStorage.getItem(MODEL_KEY)) setModel(data.default);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!open) return;
    api.get(`/ai/history/${sessionId}`).then(({ data }) => setMessages(data.messages.map((m) => ({ role: m.role, content: m.content })))).catch(() => {});
  }, [open, sessionId]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, open]);

  const pickModel = (key) => { setModel(key); localStorage.setItem(MODEL_KEY, key); };

  const reset = () => {
    localStorage.removeItem(SESSION_KEY);
    setMessages([]);
    setSessionId(getSession());
  };

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: message }, { role: "assistant", content: "", streaming: true }]);
    try {
      const res = await fetch(`${API}/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message, model }),
      });
      if (!res.ok || !res.body) throw new Error("bad response");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const ev = JSON.parse(part.slice(6));
          if (ev.delta) setMessages((m) => { const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], content: c[c.length - 1].content + ev.delta }; return c; });
          if (ev.error) setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: ev.error, error: true }; return c; });
        }
      }
      setMessages((m) => { const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], streaming: false }; return c; });
    } catch {
      setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: "The assistant is unavailable right now. Please call the workshop.", error: true }; return c; });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(!open)}
        data-testid="ai-chat-toggle"
        aria-label="Ask Sagittarius"
        className="fixed bottom-6 right-6 z-[60] flex h-14 w-14 items-center justify-center bg-amber-600 text-black shadow-[0_0_40px_rgba(217,119,6,0.35)] transition-transform duration-200 hover:scale-105"
      >
        {open ? <X size={22} /> : <MessageSquare size={22} />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.98 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="fixed bottom-[10.5rem] right-6 z-[60] flex h-[min(600px,calc(100vh-200px))] w-[min(400px,calc(100vw-48px))] flex-col border border-white/10 bg-[#0D1017] shadow-2xl"
            data-testid="ai-chat-panel"
          >
            <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
              <div className="flex items-center gap-2">
                <Sparkles size={15} className="text-amber-500" />
                <span className="font-mono text-xs uppercase tracking-[0.25em] text-slate-100">Ask Sagittarius</span>
              </div>
              <button onClick={reset} title="New conversation" data-testid="ai-chat-reset" className="p-1.5 text-slate-500 transition-colors hover:text-amber-500"><RotateCcw size={14} /></button>
            </div>

            <div className="flex border-b border-white/10" data-testid="ai-model-tabs">
              {models.map((m) => (
                <button key={m.key} onClick={() => pickModel(m.key)} data-testid={`ai-model-${m.key}`}
                  className={`flex-1 py-2 font-mono text-[10px] uppercase tracking-[0.15em] transition-colors ${model === m.key ? "bg-amber-600/15 text-amber-500" : "text-slate-500 hover:text-slate-200"}`}>
                  {m.label}
                </button>
              ))}
            </div>

            <div className="flex-1 space-y-4 overflow-y-auto px-4 py-4" data-testid="ai-chat-messages">
              {messages.length === 0 && (
                <div data-testid="ai-chat-welcome">
                  <p className="text-sm leading-relaxed text-slate-400">Kia ora — ask me anything about servicing, BMW diagnostics, nav conversions, Oil Trac or booking in.</p>
                  <div className="mt-4 flex flex-col gap-2">
                    {SUGGESTIONS.map((s) => (
                      <button key={s} onClick={() => send(s)} data-testid="ai-suggestion" className="border border-white/10 px-3 py-2 text-left text-xs text-slate-300 transition-colors hover:border-amber-600/60 hover:text-amber-500">{s}</button>
                    ))}
                  </div>
                </div>
              )}
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`ai-msg-${m.role}`}>
                  <div className={`max-w-[85%] whitespace-pre-wrap px-3.5 py-2.5 text-sm leading-relaxed ${m.role === "user" ? "bg-amber-600 text-black" : m.error ? "border border-red-500/40 bg-red-500/10 text-red-300" : "border border-white/10 bg-[#12151C] text-slate-200"}`}>
                    {m.content}{m.streaming && <span className="ml-0.5 inline-block h-3.5 w-1.5 animate-pulse bg-amber-500 align-middle" />}
                  </div>
                </div>
              ))}
              <div ref={bottomRef} />
            </div>

            <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-center gap-2 border-t border-white/10 p-3" data-testid="ai-chat-form">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type your question…"
                disabled={busy}
                data-testid="ai-chat-input"
                className="flex-1 border border-white/10 bg-[#12151C] px-3 py-2.5 text-sm text-slate-100 placeholder:text-slate-600 outline-none focus:border-amber-600/60"
              />
              <button type="submit" disabled={busy || !input.trim()} data-testid="ai-chat-send" className="flex h-10 w-10 items-center justify-center bg-amber-600 text-black transition-colors hover:bg-amber-500 disabled:opacity-50">
                {busy ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
